import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getMarket } from '@travel/i18n';
import { Info } from '@travel/icons/ui';
import type { ProviderContractType } from '@travel/traveler-core/constants';
import useDeviceType from '@travel/traveler-core/hooks/useDeviceType';
import { Alert, FlatButton } from '@travel/ui';
import { isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import Swiper from 'components/Swiper';

import { getLocation } from 'store/__router/selectors';
import { getError } from 'store/bookingStep/selectors';
import { getItem as getProviderItem } from 'store/providerInformation/selectors';
import { getItems } from 'store/providerList/selectors';
import { getItems as getPlans } from 'store/providerRatePlan/selectors';

import { PROVIDER_CONTRACT_TYPES } from 'constants/provider';
import { Translate } from 'core/translate';
import { Link } from 'core/universalRouter/Link';
import paths from 'core/universalRouter/paths';

import ProviderBox from '../../pages/ProviderListPage/components/ProviderBox';
import ItemContainer from './ItemContainer';
import PlanItem from './PlanItem';
import ProviderItem from './ProviderItem';
import RoomItem from './RoomItem';
import { RoomItemCard } from './RoomItemCard/RoomItemCard';

import styles from './guideUserPage.module.scss';

const PLANS = 'PLANS';
const ROOMS = 'ROOMS';
const PROVIDERS = 'PROVIDERS';

export const PAGE_NAME: Record<string, string> = {
  RATE_CHANGED: 'availability_error:other_available_plans_list',
  PLAN_UNAVAILABLE: 'availability_error:other_available_plans_list',
  ITEMPLAN_UNAVAILABLE: 'availability_error:other_available_rooms_list',
  ITEM_UNAVAILABLE: 'availability_error:other_available_hotels_list',
  PROVIDER_UNAVAILABLE: 'availability_error:other_available_hotels_list',
  SALES_PROMOTION_NO_LONGER_APPLICABLE: 'availability_error:other_available_plans_list',
};

export type Items = typeof PLANS | typeof ROOMS | typeof PROVIDERS;

type Props = {
  query: { itemRatePlanId: string; contractType: ProviderContractType };
};

function GuideUserPage({ query }: Props) {
  const { providers, areaName } = useSelector(getItems);
  const providerItem = useSelector(getProviderItem);
  const itemPlans = useSelector(getPlans);
  const location = useSelector(getLocation);
  const market = useSelector(getMarket);
  const errorCode = useSelector(getError)?.code || 'PLAN_UNAVAILABLE';
  const isProviderAvailable = errorCode !== 'PROVIDER_UNAVAILABLE';

  const isDirectContract = query?.contractType === PROVIDER_CONTRACT_TYPES.DIRECT;

  // if provider is unavailable we should guide user to the area which provider in
  const providerPathIdArray = providerItem?.pathId?.split('/');
  if (providerPathIdArray?.length > 2) {
    providerPathIdArray?.pop();
  }
  const newPathId = providerPathIdArray?.join('/');

  const pathId = isProviderAvailable ? providerItem?.pathId : newPathId;

  const selectedItemPlan =
    itemPlans?.find((item) => item.id === query.itemRatePlanId) || itemPlans[0];

  const ITEMS = useMemo(() => {
    return {
      ...(isDirectContract && isNotEmptyArray(selectedItemPlan?.plans) && { PLANS }),
      ...(isNotEmptyArray(itemPlans) && { ROOMS }),
      ...(isNotEmptyArray(providers) && { PROVIDERS }),
    };
  }, [itemPlans, providers, selectedItemPlan, isDirectContract]);

  const [currentItem, setCurrentItem] = useState(Object.keys(ITEMS)[0]);

  useEffect(() => {
    setCurrentItem(Object.keys(ITEMS)[0]);
  }, [ITEMS]);

  const isSP = useDeviceType() === 'sp';

  const rapPageName = PAGE_NAME[errorCode];

  const getLink = (id: string) => `${paths.providerInfo.pathResolver(id)}${location?.search || ''}`;

  const handleChangeItem = (index: number) => {
    setCurrentItem(Object.keys(ITEMS)[index]);
  };

  const path = isProviderAvailable ? paths?.providerInfo : paths?.providerList;
  const backButton = (
    <Link
      to={`${path.pathResolver(pathId || '')}${location?.search ? `?${location?.search}` : ''}`}
    >
      <FlatButton className={styles.backButton} data-testid="guideUserPage-backButton">
        <Translate id="Guide_User_Inventory.Back_to_Search" />
      </FlatButton>
    </Link>
  );

  return (
    <>
      <div className={styles.container} data-testid="guideUserPage-wrapper">
        {isDirectContract ? (
          <p className={styles.message}>
            <Info size={14} />
            <Translate id="Guide_User_Inventory.Error_Message" />
          </p>
        ) : (
          <Alert
            className={styles.alertMessageWrapper}
            titleWrapperClassName={styles.alertTitleWrapper}
            titleClassName={styles.alertTitle}
            iconClassName={styles.icon}
            type="info"
            title={<Translate id="Guide_User_Inventory.Out_To_Out.Error_Message" />}
            isClosable={false}
          />
        )}

        {/* For SP only */}
        {isSP && (
          <Swiper
            paginationType="arrows"
            onChange={(_, newIndex) => {
              handleChangeItem(newIndex);
            }}
          >
            {Object.keys(ITEMS).map((index) => (
              <div className={styles.dotContainer} key={index} />
            ))}
          </Swiper>
        )}

        {/* Other available plans */}
        {isDirectContract && isNotEmptyArray(selectedItemPlan?.plans) && (
          <ItemContainer
            title={
              <Translate
                id="Guide_User_Inventory.Suggestion_Plan"
                data={{ item_name: selectedItemPlan.name }}
              />
            }
            items={PLANS}
            currentItems={currentItem}
          >
            {selectedItemPlan.plans.map((plan) => (
              <Link to={getLink(providerItem.id)} key={plan.itemRatePlanId}>
                <PlanItem plan={plan} />
              </Link>
            ))}
          </ItemContainer>
        )}

        {/* Other available itemPlans (rooms) */}
        {isNotEmptyArray(itemPlans) && (
          <ItemContainer
            title={
              <Translate
                id="Guide_User_Inventory.Suggestion_Item"
                data={{ provider_name: providerItem.name }}
              />
            }
            items={ROOMS}
            currentItems={currentItem}
            isDirectContract={isDirectContract}
          >
            {itemPlans.map((item) => (
              <Link to={getLink(providerItem.id)} key={item.id}>
                {isDirectContract ? (
                  <RoomItem ratePlanItem={item} />
                ) : (
                  <RoomItemCard
                    ratePlanItem={item}
                    isDirectContract={isDirectContract}
                    query={query}
                  />
                )}
              </Link>
            ))}
          </ItemContainer>
        )}

        {/* Other available providers */}
        {isNotEmptyArray(providers) && (
          <ItemContainer
            title={
              <Translate
                id="Guide_User_Inventory.Suggestion_Provider"
                data={{ area_name: areaName }}
              />
            }
            items={PROVIDERS}
            currentItems={currentItem}
            isDirectContract={isDirectContract}
          >
            {providers.map((provider) => (
              <Link to={getLink(provider.id)} key={provider.id}>
                {isDirectContract ? (
                  <ProviderItem market={market.marketCode} provider={provider} />
                ) : (
                  <div className={styles.providerBox}>
                    <ProviderBox
                      item={provider}
                      index={+provider.id}
                      isExpandedMapStyle
                      onPage="providerListDated"
                      shouldHideRoomAndPlanBtn
                      customSwiperStyle={styles.providerBoxSwiper}
                    />
                  </div>
                )}
              </Link>
            ))}
          </ItemContainer>
        )}

        {!isSP && backButton}
      </div>
      {isSP && <div className={styles.buttonContainer}>{backButton}</div>}
      <DataLayer
        pageName={rapPageName}
        pageType="availability_error"
        siteSection="availability_error"
      />
    </>
  );
}

export default GuideUserPage;
